import React from 'react';
import { useNavigate } from 'react-router-dom';
import useAllGamesPage from '../../../../hooks/useAllGamesPage';
import GameCard from './gameCard';
import { Button } from '../../../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../../ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../../ui/dialog';
import { Alert, AlertDescription } from '../../../ui/alert';
import BreadcrumbNav, { BreadcrumbItemProps } from '../../../common/BreadcrumbNav';

/**
 * Component to display the "All Games" page, which provides functionality to view, create, and join games.
 * @returns A React component that includes:
 * - A "Create Game" button to open a modal for selecting a game type.
 * - A list of available games, each rendered using the `GameCard` component.
 * - A refresh button to reload the list of available games from the server.
 */
const AllGamesPage = () => {
  const {
    availableGames,
    handleJoin,
    fetchGames,
    isModalOpen,
    handleToggleModal,
    handleSelectGameType,
    error,
  } = useAllGamesPage();

  const navigate = useNavigate();

  // Define breadcrumb items
  const breadcrumbItems: BreadcrumbItemProps[] = [
    { label: 'Home', href: '/home' },
    { label: 'Games' },
  ];

  return (
    <div className='container mx-auto p-6'>
      {/* Breadcrumb Navigation */}
      <BreadcrumbNav items={breadcrumbItems} />

      <div className='mb-6'>
        <Button
          variant='default'
          onClick={handleToggleModal}
          className='bg-blue-600 hover:bg-blue-700'>
          Create Game
        </Button>
      </div>

      <Dialog open={isModalOpen} onOpenChange={handleToggleModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Select Game Type</DialogTitle>
          </DialogHeader>
          <div className='grid gap-4 py-4'>
            <Button onClick={() => handleSelectGameType('Nim')} className='w-full'>
              Nim
            </Button>
            <Button
              onClick={() => handleSelectGameType('CodeQuiz')}
              className='w-full bg-indigo-600 hover:bg-indigo-700'>
              Code Quiz
            </Button>
          </div>
          <DialogFooter>
            <Button variant='outline' onClick={handleToggleModal}>
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Card className='shadow-md'>
        <CardHeader className='flex flex-row items-center justify-between pb-2'>
          <CardTitle className='text-xl'>Available Games</CardTitle>
          <div className='flex gap-2'>
            <Button variant='outline' onClick={fetchGames} size='sm' className='ml-auto'>
              Refresh List
            </Button>
            <Button
              variant='outline'
              size='sm'
              onClick={() => navigate('/games/invitations')}
              className='ml-auto text-blue-600 border-blue-600'>
              My Invitations
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {error && (
            <Alert variant='destructive' className='mb-4'>
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
          <div className='space-y-4'>
            {availableGames.map(game => (
              <GameCard key={game.gameID} game={game} handleJoin={handleJoin} />
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AllGamesPage;
